import { CHARTER_FLIGHT_DETAILS, MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import { FlightViewTypes, FlightsClassTypes } from 'constants/flight'
import { isFlashHotelOffer } from 'lib/offer/offerTypes'
import { formatReturnFlightsGuestLabel } from 'lib/offer/occupancyUtils'
import type { IntlShape } from 'react-intl'

const RETURN_FARE_FREE_ORIGIN_AIRPORT = 'MLE'
const RETURN_FARE_FREE_DESTINATION_AIRPORT = 'MEL'

export function isMaldivesCharterBundleOffer(
  offer: App.AnyOffer | undefined,
  isMaldivesCharterEnabled: boolean,
  { requireFlightsEnabled = true }: { requireFlightsEnabled?: boolean } = {},
): offer is App.HotelOffer | App.HotelOfferSummary {
  return (
    isFlashHotelOffer(offer) &&
    isMaldivesCharterEnabled &&
    (!requireFlightsEnabled || offer.offerFlightsEnabled) &&
    offer.campaignTags.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)
  )
}

export function isConfiguredFeederOriginAirportCode(
  charterBundleRoute: App.CharterBundleRoute | undefined,
  airportCode: string | undefined,
): boolean {
  if (!charterBundleRoute || !airportCode) {
    return false
  }

  const routeInfo = CHARTER_FLIGHT_DETAILS[charterBundleRoute]
  return routeInfo.feederFlightOrigins.some(airport => airport.airportCode === airportCode)
}

export function getFeederOriginAirportCode(
  charterBundleRoute: App.CharterBundleRoute | undefined,
  flightOrigin: App.Airport | undefined,
): string | undefined {
  if (!charterBundleRoute || !flightOrigin) {
    return undefined
  }

  const routeInfo = CHARTER_FLIGHT_DETAILS[charterBundleRoute]
  return flightOrigin.code !== routeInfo.originAirport.airportCode &&
    isConfiguredFeederOriginAirportCode(charterBundleRoute, flightOrigin.code)
    ? flightOrigin.code
    : undefined
}

/**
 * Returns true when at least one of the two charter legs carries a positive
 * `feederEstimatedPrice`. svc-flights only attaches this field when the search was
 * eligible (charter source + economy + non-MEL feeder origin) AND the FDA returned
 * a feeder price for that leg/date. When both legs are missing or sum to 0 the
 * bundle is effectively a non-feeder charter for those dates.
 */
export function hasFeederFlightPriced(
  departingJourney: Pick<App.JourneyV2, 'feederEstimatedPrice'> | undefined,
  returningJourney: Pick<App.JourneyV2, 'feederEstimatedPrice'> | undefined,
): boolean {
  const total = (departingJourney?.feederEstimatedPrice ?? 0) + (returningJourney?.feederEstimatedPrice ?? 0)
  return total > 0
}

export function hasReturnFareFree(journey: App.JourneyV2 | undefined): boolean {
  const flights = journey?.flightGroup.flights
  const firstFlight = flights?.[0]
  const lastFlight = flights?.at(-1)
  const adultPrice = journey?.price.adult

  return journey?.fareType === FlightViewTypes.RETURN &&
    journey.cabin === FlightsClassTypes.ECONOMY &&
    firstFlight?.departureAirport === RETURN_FARE_FREE_ORIGIN_AIRPORT &&
    lastFlight?.arrivalAirport === RETURN_FARE_FREE_DESTINATION_AIRPORT &&
    adultPrice?.totalFare === 0
}

export function shouldShowReturnFareFree({
  charterBundleRoute,
  hasSelectedDates,
  isSaleEnabled,
  feederAirportCode,
  hasReturnFareFree,
}: {
  charterBundleRoute: App.CharterBundleRoute | undefined
  hasSelectedDates: boolean
  isSaleEnabled: boolean
  feederAirportCode: string | undefined
  hasReturnFareFree: boolean | undefined
}): boolean {
  return !!charterBundleRoute &&
    hasSelectedDates &&
    (isSaleEnabled || !feederAirportCode) &&
    !!hasReturnFareFree
}

export function canAddCharterBundleToUserCart({
  charterBundleRoute,
  flightOrigin,
}: {
  charterBundleRoute?: App.CharterBundleRoute
  flightOrigin?: App.Airport
}): boolean {
  if (!charterBundleRoute) {
    return true
  }

  if (!flightOrigin) {
    return false
  }

  const routeInfo = CHARTER_FLIGHT_DETAILS[charterBundleRoute]
  return flightOrigin.code === routeInfo.originAirport.airportCode
}

interface RewriteCharterFeederInclusionsParams {
  feederCity: string
  originCity?: string
  destinationCity?: string
  intl: IntlShape
}

export function rewriteCharterFeederInclusions<T extends { description: string }>(
  inclusions: Array<T>,
  {
    feederCity,
    originCity,
    destinationCity,
    intl,
  }: RewriteCharterFeederInclusionsParams,
): Array<T> {
  if (!originCity || !destinationCity) {
    return inclusions
  }

  return inclusions.map(inclusion => {
    const isCharterFlightInclusion = inclusion.description.includes(originCity) &&
      inclusion.description.includes(destinationCity)

    if (!isCharterFlightInclusion) {
      return inclusion
    }

    return {
      ...inclusion,
      description: intl.formatMessage(
        {
          id: 'WqwAp2Rk1_YdWyK0xDOI',
          defaultMessage: 'Return flights from {feederCity} to {destination}, via {origin}',
          description: 'Charter bundle feeder route inclusion on hotel search tile when a non-Melbourne feeder departure airport is selected',
        },
        {
          feederCity,
          destination: destinationCity,
          origin: originCity,
        },
      ),
    }
  })
}

interface RewriteCharterReturnFlightsGuestInclusionsParams {
  rooms: Array<App.Occupants>
  intl: IntlShape
}

function isCharterFlightRouteInclusion(inclusion: { description: string; symbol?: string }): boolean {
  const description = inclusion.description
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLocaleLowerCase()

  return inclusion.symbol === 'plane_with_arrows' &&
    description.includes('return') &&
    description.includes('melbourne') &&
    description.includes('male')
}

/**
 * Replaces the Maldives charter "Direct return flights from Melbourne to Malé"
 * (or feeder-rewritten) inclusion copy with occupancy-based return-flights wording,
 * while keeping the airplane icon.
 */
export function rewriteCharterReturnFlightsGuestInclusions<T extends { description: string; symbol?: string }>(
  inclusions: Array<T>,
  {
    rooms,
    intl,
  }: RewriteCharterReturnFlightsGuestInclusionsParams,
): Array<T> {
  const guestCountLabel = formatReturnFlightsGuestLabel(rooms)
  if (!guestCountLabel) {
    return inclusions
  }

  let hasRewrittenCharterFlight = false

  return inclusions.map(inclusion => {
    if (hasRewrittenCharterFlight || !isCharterFlightRouteInclusion(inclusion)) {
      return inclusion
    }

    hasRewrittenCharterFlight = true

    return {
      ...inclusion,
      description: intl.formatMessage(
        {
          id: 'mXDPkuZjxmPWqEv75K09',
          defaultMessage: 'Return flights for {guestCountLabel}',
          description: 'Inclusion shown on a hotel rate tile when flights are toggled on',
        },
        { guestCountLabel },
      ),
      symbol: inclusion.symbol ?? 'plane_with_arrows',
    }
  })
}
